import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import {
  SCOPES, DURATIONS, ON_LEAVE_CHURCH, ON_LEAVE_WORSHIP_TEAM,
  ON_THIRD_PARTY_PUBLISH, RENEWAL, computeExpiry, summarise,
} from '../../lib/licence'
import { Shell } from '../../components/Shell'
import { Button, Check, Field, Progress, Spinner } from '../../components/ui'

const TOTAL = 4

/**
 * Endorsing a song means granting a church a licence to use it — never
 * handing the song over. Copyright stays with the songwriter throughout;
 * this screen only sets the terms of use.
 */
export default function Endorse() {
  const { id, step } = useParams()
  const nav = useNavigate()
  const { user, church } = useSession()

  const [song, setSong] = useState(null)
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(null)

  // The draft lives in local state across all four steps and is only ever
  // written to song_licences once, on confirm — there is nothing to
  // autosave, because nothing is agreed until the songwriter says so.
  const [draft, setDraft] = useState({
    scope_rehearsal: false, scope_live: false, scope_recording: false,
    scope_streaming: false, scope_print: false,
    duration_months: DURATIONS[1].months,
    on_leave_church: null,
    on_leave_worship_team: null,
    wind_down_days: 60,
    on_third_party_publish: null,
    renewal: null,
  })

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('songs').select('*').eq('id', id).maybeSingle()
    if (error) setErr(readableError(error))
    setSong(data ?? null)
    setReady(true)
  }, [id])

  useEffect(() => { load() }, [load])

  if (!ready) return <Shell hidePlayer><Spinner /></Shell>
  if (!song) return (
    <Shell hidePlayer back="/songs" title="Your songs">
      <h2 className="q">We can't find that song.</h2>
    </Shell>
  )
  if (song.songwriter_id !== user.id) return (
    <Shell hidePlayer back="/songs" title="Your songs"><p className="sub">This isn't your song.</p></Shell>
  )
  if (!church) return (
    <Shell hidePlayer back="/songs" title="Your songs">
      <h2 className="q">No church to endorse it to.</h2>
      <p className="sub">You need an approved church membership before you can set licence terms.</p>
    </Shell>
  )

  const go = (n) => nav(`/songs/${id}/endorse/${n}`)
  const common = { song, church, draft, setDraft, go, err, setErr }

  if (step === 'done') return <Done song={song} />
  const n = Math.min(Math.max(parseInt(step, 10) || 1, 1), TOTAL)
  const Step = [E1, E2, E3, E4][n - 1]
  return <Step {...common} n={n} />
}

/** The reminder that belongs on every step, not just the first. */
function CopyrightNote() {
  return (
    <p className="small" style={{ marginTop: 4 }}>
      You keep the copyright. This only grants your church a licence to use
      the song on the terms you set here — it can be narrowed or revoked later.
    </p>
  )
}

/* ------------------------------------------------------------------- E1 */
function E1({ draft, setDraft, go, n }) {
  const anyScope = SCOPES.some((s) => draft[s.column])

  return (
    <Shell hidePlayer back="/songs" title="Your songs">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">Licence terms</div>
      <h2 className="q">What can they do, and for how long?</h2>
      <CopyrightNote />

      <p className="small" style={{ marginTop: 16, fontWeight: 600 }}>Ways it can be used</p>
      <div className="stack" style={{ marginTop: 6 }}>
        {SCOPES.map((s) => (
          <button key={s.id} className={`chip chip-wide${draft[s.column] ? ' on' : ''}`}
                  onClick={() => setDraft({ ...draft, [s.column]: !draft[s.column] })}>
            <span style={{ flex: 1 }}>{s.label}<small>{s.blurb}</small></span>
            {draft[s.column] ? <Check /> : '○'}
          </button>
        ))}
      </div>

      <p className="small" style={{ marginTop: 18, fontWeight: 600 }}>How long it runs</p>
      <div className="stack" style={{ marginTop: 6 }}>
        {DURATIONS.map((d) => (
          <button key={d.id} className={`chip chip-wide${draft.duration_months === d.months ? ' on' : ''}`}
                  onClick={() => setDraft({ ...draft, duration_months: d.months })}>
            <span style={{ flex: 1 }}>{d.label}<small>{d.blurb}</small></span>
            {draft.duration_months === d.months ? <Check /> : '○'}
          </button>
        ))}
      </div>

      <div className="hr" />
      <button className="btn btn-primary" disabled={!anyScope} onClick={() => go(2)}>
        Next — if things change
      </button>
    </Shell>
  )
}

/* ------------------------------------------------------------------- E2 */
function E2({ song, draft, setDraft, go, n }) {
  const ok = draft.on_leave_church && draft.on_leave_worship_team &&
    (!needsDays(draft) || (draft.wind_down_days > 0))

  const needsWindDown = draft.on_leave_church === 'wind_down' || draft.on_leave_worship_team === 'wind_down'

  return (
    <Shell hidePlayer back={`/songs/${song.id}/endorse/1`} title="Back">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">Licence terms</div>
      <h2 className="q">If things change.</h2>
      <CopyrightNote />

      <p className="small" style={{ marginTop: 16, fontWeight: 600 }}>
        If you leave the church entirely
      </p>
      <div className="stack" style={{ marginTop: 6 }}>
        {ON_LEAVE_CHURCH.map((o) => (
          <button key={o.id} className={`chip chip-wide${draft.on_leave_church === o.id ? ' on' : ''}`}
                  onClick={() => setDraft({ ...draft, on_leave_church: o.id })}>
            <span style={{ flex: 1 }}>{o.label}<small>{o.blurb}</small></span>
            {draft.on_leave_church === o.id ? <Check /> : '○'}
          </button>
        ))}
      </div>

      <p className="small" style={{ marginTop: 18, fontWeight: 600 }}>
        If you stay at the church, but come off the worship team
      </p>
      <p className="small" style={{ marginTop: -2, marginBottom: 6 }}>
        A separate case from leaving altogether — this is for when you're still a member here.
      </p>
      <div className="stack" style={{ marginTop: 6 }}>
        {ON_LEAVE_WORSHIP_TEAM.map((o) => (
          <button key={o.id} className={`chip chip-wide${draft.on_leave_worship_team === o.id ? ' on' : ''}`}
                  onClick={() => setDraft({ ...draft, on_leave_worship_team: o.id })}>
            <span style={{ flex: 1 }}>{o.label}<small>{o.blurb}</small></span>
            {draft.on_leave_worship_team === o.id ? <Check /> : '○'}
          </button>
        ))}
      </div>

      {needsWindDown && (
        <div style={{ marginTop: 14 }}>
          <Field label="Wind-down period, in days">
            <input className="input" type="number" min="1" inputMode="numeric"
                   value={draft.wind_down_days}
                   onChange={(e) => setDraft({ ...draft, wind_down_days: Math.max(1, Number(e.target.value) || 0) })} />
          </Field>
        </div>
      )}

      <div className="hr" />
      <button className="btn btn-primary" disabled={!ok} onClick={() => go(3)}>
        Next — publishing &amp; renewal
      </button>
    </Shell>
  )
}

const needsDays = (draft) =>
  draft.on_leave_church === 'wind_down' || draft.on_leave_worship_team === 'wind_down'

/* ------------------------------------------------------------------- E3 */
function E3({ song, draft, setDraft, go, n }) {
  const ok = draft.on_third_party_publish && draft.renewal

  return (
    <Shell hidePlayer back={`/songs/${song.id}/endorse/2`} title="Back">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">Licence terms</div>
      <h2 className="q">Publishing and renewal.</h2>
      <CopyrightNote />

      <p className="small" style={{ marginTop: 16, fontWeight: 600 }}>
        If the song is later signed by a publisher, or registered with CCLI or APRA
      </p>
      <div className="stack" style={{ marginTop: 6 }}>
        {ON_THIRD_PARTY_PUBLISH.map((o) => (
          <button key={o.id} className={`chip chip-wide${draft.on_third_party_publish === o.id ? ' on' : ''}`}
                  onClick={() => setDraft({ ...draft, on_third_party_publish: o.id })}>
            <span style={{ flex: 1 }}>{o.label}<small>{o.blurb}</small></span>
            {draft.on_third_party_publish === o.id ? <Check /> : '○'}
          </button>
        ))}
      </div>

      <p className="small" style={{ marginTop: 18, fontWeight: 600 }}>When the term is up</p>
      <div className="stack" style={{ marginTop: 6 }}>
        {RENEWAL.map((o) => (
          <button key={o.id} className={`chip chip-wide${draft.renewal === o.id ? ' on' : ''}`}
                  onClick={() => setDraft({ ...draft, renewal: o.id })}>
            <span style={{ flex: 1 }}>{o.label}<small>{o.blurb}</small></span>
            {draft.renewal === o.id ? <Check /> : '○'}
          </button>
        ))}
      </div>

      <div className="hr" />
      <button className="btn btn-primary" disabled={!ok} onClick={() => go(4)}>
        Next — read it back
      </button>
    </Shell>
  )
}

/* ------------------------------------------------------------------- E4 */
function E4({ song, church, draft, go, err, setErr }) {
  const { user } = useSession()
  const [busy, setBusy] = useState(false)
  const expiry = computeExpiry(new Date(), draft.duration_months)
  const lines = summarise({
    ...draft,
    expires_at: expiry ? expiry.toISOString() : null,
  })

  const confirm = async () => {
    setBusy(true); setErr(null)
    const startsAt = new Date()
    const { error } = await supabase.from('song_licences').insert({
      song_id: song.id,
      church_id: church.id,
      songwriter_id: user.id,
      duration_months: draft.duration_months,
      starts_at: startsAt.toISOString(),
      expires_at: computeExpiry(startsAt, draft.duration_months)?.toISOString() ?? null,
      scope_rehearsal: draft.scope_rehearsal,
      scope_live: draft.scope_live,
      scope_recording: draft.scope_recording,
      scope_streaming: draft.scope_streaming,
      scope_print: draft.scope_print,
      on_leave_church: draft.on_leave_church,
      on_leave_worship_team: draft.on_leave_worship_team,
      wind_down_days: needsDays(draft) ? draft.wind_down_days : null,
      on_third_party_publish: draft.on_third_party_publish,
      renewal: draft.renewal,
      status: 'proposed',
      proposed_at: startsAt.toISOString(),
      terms_snapshot: draft,
    })
    setBusy(false)
    if (error) return setErr(readableError(error))
    go('done')
  }

  return (
    <Shell hidePlayer back={`/songs/${song.id}/endorse/3`} title="Back">
      <Progress step={4} total={TOTAL} />
      <div className="eyebrow">Licence terms</div>
      <h2 className="q">Here's what you're proposing.</h2>
      <p className="sub">
        {church.name} will need to accept these terms before anything changes.
        You keep the copyright throughout — this is a licence, not a transfer.
      </p>

      <div className="stack" style={{ marginTop: 6 }}>
        {lines.map((line, i) => (
          <div className="tile" key={i} style={{ margin: 0 }}>
            <p style={{ margin: 0 }}>{line}</p>
          </div>
        ))}
      </div>

      {err && <p className="err">{err}</p>}
      <div className="hr" />
      <Button busy={busy} onClick={confirm}>Propose these terms to {church.name}</Button>
    </Shell>
  )
}

/* ---------------------------------------------------------------- done */
function Done({ song }) {
  const nav = useNavigate()
  return (
    <Shell hidePlayer back="/songs" title="Your songs">
      <div className="done-ic"><Check size={26} w={2.4} /></div>
      <h2 className="q center">Terms sent.</h2>
      <p className="sub center">
        Your church can now accept or decline. Nothing changes until they do,
        and the song stays yours either way.
      </p>
      <div className="hr" />
      <button className="btn btn-primary" onClick={() => nav(`/songs/${song.id}`)}>Back to the song</button>
    </Shell>
  )
}
